
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, adminOperation } from '../../utils/supabase';
import type { Shelter, ShelterEvent } from '../../utils/supabase';

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState<ShelterEvent | null>(null);
  const [shelter, setShelter] = useState<Shelter | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAttendModal, setShowAttendModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [attendeeForm, setAttendeeForm] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (eventId) {
      loadEventData();
    }
  }, [eventId]);

  const loadEventData = async () => {
    try {
      // Cargar evento
      const { data: eventData } = await supabase
        .from('shelter_events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (eventData) {
        setEvent(eventData);

        // Cargar refugio
        const { data: shelterData } = await supabase
          .from('shelters')
          .select('*')
          .eq('id', eventData.shelter_id)
          .single();

        setShelter(shelterData);
      }
    } catch (error) {
      console.error('Error cargando evento:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttendEvent = () => {
    setShowAttendModal(true);
  };

  const submitAttendance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event) return;

    setSubmitting(true);

    try {
      // Registrar asistencia
      const { error: attendeeError } = await supabase
        .from('event_attendees')
        .insert([{
          event_id: event.id,
          attendee_name: attendeeForm.name,
          attendee_phone: attendeeForm.phone || null,
          attendee_email: attendeeForm.email || null
        }]);

      if (attendeeError) throw attendeeError;

      // Actualizar contador de asistentes
      const newCount = event.attendees_count + 1;
      const { error: updateError } = await adminOperation('update', 'shelter_events', {
        attendees_count: newCount
      }, event.id);

      if (updateError) throw updateError;

      // Actualizar estado local
      setEvent(prev => prev ? { ...prev, attendees_count: newCount } : null);

      setShowAttendModal(false);
      setAttendeeForm({ name: '', phone: '', email: '' });
      alert('¡Gracias por confirmar tu asistencia! Te esperamos en el evento.');

    } catch (error) {
      console.error('Error registrando asistencia:', error);
      alert('Error al registrar tu asistencia. Por favor intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando evento...</p>
        </div>
      </div>
    );
  }

  if (!event || !shelter) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Evento no encontrado</h2>
          <p className="text-gray-600 mb-6">El evento que buscas no existe o ha sido eliminado.</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  // Generar array de imágenes del evento
  const eventImages = [
    event.image_url_1,
    event.image_url_2,
    event.image_url_3,
    event.image_url_4,
    event.image_url_5
  ].filter(Boolean);

  // Si no hay imágenes del evento, usar imagen por defecto
  const displayImages = eventImages.length > 0 ? eventImages : [
    'https://readdy.ai/api/search-image?query=Animal%20shelter%20community%20event%20with%20volunteers%20and%20rescued%20dogs%2C%20outdoor%20gathering%20with%20people%20helping%20animals%2C%20warm%20community%20atmosphere%20with%20banners%20and%20activities%20for%20pet%20adoption%20awareness&width=800&height=600&seq=event-detail-1&orientation=landscape'
  ];

  const eventDate = new Date(event.event_date);
  const isToday = eventDate.toDateString() === new Date().toDateString();
  const isTomorrow = eventDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
  const isPastEvent = eventDate <= new Date();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <button 
              onClick={() => navigate(`/shelter/${shelter.id}/events`)}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Volver a Eventos</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <img 
                src="/images/image.png" 
                alt="Huellitas Unidas Logo" 
                className="w-8 h-8 object-contain"
              />
              <h1 className="text-lg font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                Huellitas Unidas
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/')}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-home-line text-xl text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">{event.title}</h1>
            <div className={`px-4 py-2 rounded-full text-sm font-bold ${
              isToday ? 'bg-red-500 text-white' : 
              isTomorrow ? 'bg-orange-500 text-white' : 
              isPastEvent ? 'bg-gray-500 text-white' :
              'bg-green-500 text-white'
            }`}>
              {isToday ? '¡HOY!' : isTomorrow ? 'MAÑANA' : isPastEvent ? 'FINALIZADO' : 'PRÓXIMO'}
            </div>
          </div>
          
          <div className="flex items-center text-gray-600 space-x-6 mb-6">
            <span className="flex items-center">
              <i className="ri-building-fill mr-2 text-purple-500"></i>
              {shelter.name}
            </span>
            <span className="flex items-center">
              <i className="ri-map-pin-fill mr-2 text-red-500"></i>
              {event.location}
            </span>
            <span className="flex items-center">
              <i className="ri-group-fill mr-2 text-blue-500"></i>
              {event.attendees_count} confirmados
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={displayImages[currentImageIndex]}
                  alt={`${event.title} - Imagen ${currentImageIndex + 1}`}
                  className="w-full h-96 object-contain bg-black rounded-2xl"
                />
                
                {/* Image counter */}
                {displayImages.length > 1 && (
                  <div className="absolute top-4 right-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {displayImages.length}
                  </div>
                )}
                
                {/* Navigation arrows - Only show if multiple images */}
                {displayImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : displayImages.length - 1)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full shadow-lg transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-gray-800"></i>
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex(currentImageIndex < displayImages.length - 1 ? currentImageIndex + 1 : 0)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full shadow-lg transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-right-line text-gray-800"></i>
                    </button>
                  </>
                )}
              </div>
              
              {/* Thumbnail Gallery - Only show if multiple images */}
              {displayImages.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {displayImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative h-20 rounded-lg overflow-hidden cursor-pointer ${
                        currentImageIndex === index ? 'ring-2 ring-purple-500' : ''
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${event.title} - Miniatura ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {currentImageIndex !== index && (
                        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Event Description */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Sobre este evento</h2>
              <div className="prose prose-lg max-w-none text-gray-700">
                <p className="leading-relaxed">{event.description}</p>
              </div>
            </div>

            {/* Event Details */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Detalles del Evento</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <i className="ri-calendar-event-fill text-purple-500 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Fecha y Hora</h3>
                      <p className="text-gray-700 font-medium">
                        {eventDate.toLocaleDateString('es-ES', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      <p className="text-purple-600 font-medium">
                        {eventDate.toLocaleTimeString('es-ES', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <i className="ri-map-pin-fill text-red-500 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Ubicación</h3>
                      <p className="text-gray-700 font-medium">{event.location}</p>
                      <p className="text-gray-600 text-sm">{event.address}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-group-fill text-blue-500 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Asistentes</h3>
                      <p className="text-blue-600 font-bold text-2xl">{event.attendees_count}</p>
                      <p className="text-gray-600 text-sm">
                        persona{event.attendees_count !== 1 ? 's' : ''} confirmada{event.attendees_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-building-fill text-green-500 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Organizador</h3>
                      <p className="text-gray-700 font-medium">{shelter.name}</p>
                      <p className="text-gray-600 text-sm">{shelter.location}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Action Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Attendance Card */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-calendar-event-fill text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {isPastEvent ? 'Evento Finalizado' : '¿Te gustaría asistir?'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {isPastEvent 
                      ? 'Este evento ya ha terminado' 
                      : 'Confirma tu asistencia y ayuda a los perritos'
                    }
                  </p>
                </div>

                {!isPastEvent ? (
                  <button
                    onClick={handleAttendEvent}
                    className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-hand-heart-fill mr-2"></i>
                    ¡Asistiré!
                  </button>
                ) : (
                  <div className="w-full bg-gray-100 text-gray-600 py-4 px-6 rounded-xl font-bold text-lg text-center">
                    <i className="ri-check-double-line mr-2"></i>
                    Evento Finalizado
                  </div>
                )}

                <div className="mt-6 pt-6 border-t">
                  <div className="text-center text-sm text-gray-600">
                    <i className="ri-shield-check-fill text-green-500 mr-1"></i>
                    Evento verificado por {shelter.name}
                  </div>
                </div>
              </div>

              {/* Shelter Info */}
              <div className="bg-purple-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-purple-900 mb-4">Información del Refugio</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                      <i className="ri-building-fill text-white"></i>
                    </div>
                    <div>
                      <p className="font-medium text-purple-900">{shelter.name}</p>
                      <p className="text-sm text-purple-700">Refugio Certificado</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <i className="ri-map-pin-fill text-red-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-purple-900">{shelter.location}</p>
                      <p className="text-sm text-purple-700">Ubicación</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-heart-fill text-green-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-purple-900">{shelter.current_dogs} perritos</p>
                      <p className="text-sm text-purple-700">Bajo cuidado</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => navigate(`/shelter/${shelter.id}`)}
                  className="w-full mt-4 bg-purple-500 hover:bg-purple-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-building-line mr-2"></i>
                  Ver Refugio
                </button>
              </div>

              {/* Quick Actions */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Otras acciones</h3>
                <div className="space-y-3">
                  <button 
                    onClick={() => navigate(`/shelter/${shelter.id}/events`)}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white transition-colors cursor-pointer"
                  >
                    <i className="ri-calendar-event-line text-purple-500"></i>
                    <span className="text-gray-700">Ver más eventos</span>
                  </button>
                  <button 
                    onClick={() => navigate('/', { state: { activeTab: 'adoptar' } })}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white transition-colors cursor-pointer"
                  >
                    <i className="ri-home-heart-line text-blue-500"></i>
                    <span className="text-gray-700">Ver adopciones</span>
                  </button>
                  <button 
                    onClick={() => navigate('/', { state: { activeTab: 'donar' } })}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white transition-colors cursor-pointer"
                  >
                    <i className="ri-heart-line text-orange-500"></i>
                    <span className="text-gray-700">Ver donaciones</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal de Asistencia */}
      {showAttendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-event-fill text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Confirmar Asistencia
              </h3>
              <p className="text-gray-600">
                {event.title}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {eventDate.toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>

            <form onSubmit={submitAttendance}>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={attendeeForm.name}
                    onChange={(e) => setAttendeeForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono (opcional)
                  </label>
                  <input
                    type="tel"
                    value={attendeeForm.phone}
                    onChange={(e) => setAttendeeForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Tu número de teléfono"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email (opcional)
                  </label>
                  <input
                    type="email"
                    value={attendeeForm.email}
                    onChange={(e) => setAttendeeForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div className="bg-purple-50 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-purple-500 text-lg mt-0.5"></i>
                  <div className="text-sm text-purple-700">
                    <p className="font-semibold mb-1">Información importante:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Tu asistencia será confirmada inmediatamente</li>
                      <li>• El refugio podrá contactarte si es necesario</li>
                      <li>• Ayuda a los perritos participando en el evento</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAttendModal(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Confirmando...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line mr-2"></i>
                      Confirmar
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
